import { useEffect, useMemo, useRef } from 'react'

import { useEvents } from '../../../shared/hooks/useEvents'
import { useLightMode } from '@/hooks/useLightMode'

import { AnimatePresence, motion } from 'framer-motion'
import { throttle } from 'lodash'

import {
  Box as DarkBox,
  Flex as DarkFlex,
  Spinner,
  tokenPrefix,
} from '@hivewatch/dark'

import { useAppSelector } from '@/redux/hooks'
import { selectIncidentTraceLogDrawerState } from '@/redux/ui/uiSlice'

import { EventFiltersI } from '../../../shared/types/traceLogTypes'
import {
  filterEventsForIncidentTab,
  getResolvedActionEventRefs,
  isHighlightedFromResolution,
  matchesResolvedActionEventRef,
} from '../../../shared/utils/traceLogUtils'
import { EventItem } from './EventItem'

const onEndReachedThreshold = 0.95

interface EventFeedPropsI extends EventFiltersI {
  showOnlyIncidentRelatedEvents?: boolean
}

export const EventFeed = ({
  incident,
  filters,
  showOnlyIncidentRelatedEvents = false,
}: EventFeedPropsI) => {
  const ref = useRef<HTMLDivElement>(null)
  const { highlightedEvent } = useAppSelector(selectIncidentTraceLogDrawerState)
  const mode = useLightMode()

  // For the Incident tab (showOnlyIncidentRelatedEvents=true), don't center on
  // highlightedEvent - use incident.detectedAt so all related events are fetched.
  // For Device/Facility tabs, center on highlightedEvent if user clicked an EVENT_REF.
  const centerTimestamp = showOnlyIncidentRelatedEvents
    ? undefined
    : highlightedEvent?.detectedAt

  const { isLoading, events, getNewerEvents, getOlderEvents, hasOlder } =
    useEvents({
      incident,
      filters,
      centerTimestamp,
    })

  // useEvents re-creates its fetchers every render (they set state mid-fetch),
  // so a throttle keyed on them would be rebuilt — and its timer reset — by the
  // very re-renders it guards. Keep one stable instance and read the latest
  // fetchers/hasOlder through refs instead.
  const scrollDepsRef = useRef({ getNewerEvents, getOlderEvents, hasOlder })
  scrollDepsRef.current = { getNewerEvents, getOlderEvents, hasOlder }

  const onScroll = useMemo(
    () =>
      throttle(async () => {
        if (ref?.current) {
          const { scrollTop, scrollHeight, clientHeight } = ref.current

          // Scrolled to the top
          if (scrollTop <= 0) {
            const hasMoreEvents = await scrollDepsRef.current.getNewerEvents()
            if (hasMoreEvents) {
              ref?.current?.scrollTo({ top: 50, behavior: 'auto' })
            }
          }
          // Scrolled to the bottom
          const scrollPosition = scrollTop + clientHeight
          const endScrollThreshold = scrollHeight * onEndReachedThreshold
          if (
            scrollPosition >= endScrollThreshold &&
            scrollDepsRef.current.hasOlder
          ) {
            await scrollDepsRef.current.getOlderEvents()
          }
        }
      }, 200),
    []
  )

  useEffect(() => () => onScroll.cancel(), [onScroll])

  const eventExternalIdMatchesIncident = (eventExternalId: string) =>
    incident?.eventExternalIds?.includes(eventExternalId) ?? false

  const resolvedActionEventRefs = useMemo(
    () => getResolvedActionEventRefs(incident?.actions),
    [incident?.actions]
  )

  const filteredEvents = useMemo(
    () =>
      filterEventsForIncidentTab(
        events,
        showOnlyIncidentRelatedEvents,
        incident?.eventExternalIds,
        resolvedActionEventRefs
      ),
    [
      events,
      showOnlyIncidentRelatedEvents,
      incident?.eventExternalIds,
      resolvedActionEventRefs,
    ]
  )

  const eventItems = (
    <AnimatePresence>
      {filteredEvents?.map((e) => (
        <motion.div
          animate={{ x: 0, opacity: 1 }}
          exit={{ opacity: 0 }}
          initial={{ x: -200, opacity: 0 }}
          key={e.node.externalId}
        >
          <EventItem
            event={e.node}
            isCauseOfIncident={eventExternalIdMatchesIncident(
              e.node.externalId
            )}
            isFromResolvedAction={matchesResolvedActionEventRef(
              e.node,
              resolvedActionEventRefs
            )}
            isHighlightedFromResolution={isHighlightedFromResolution(
              e.node,
              highlightedEvent
            )}
            key={e.node?.externalId}
            shouldShowDeviceInfo={Boolean(filters?.facilityId)}
          />
        </motion.div>
      ))}
    </AnimatePresence>
  )

  const prefix = tokenPrefix(mode)
  return (
    <DarkFlex
      direction='column'
      flex='1 1 0%'
      gap='8px'
      minH={0}
      onScroll={onScroll}
      overflowX='hidden'
      overflowY='auto'
      px='24px'
      py='16px'
      ref={ref}
    >
      {eventItems}
      {isLoading && (
        <DarkBox
          alignItems='center'
          bg={`${prefix}bg.card-elevated`}
          border='1px solid'
          borderColor={`${prefix}border.subtle`}
          display='flex'
          height='80px'
          justifyContent='center'
          left='calc(50% - 40px)'
          pointerEvents='none'
          position='absolute'
          top='calc(50% - 40px)'
          width='80px'
        >
          <Spinner borderWidth='4px' colorScheme={mode} size='lg' />
        </DarkBox>
      )}
    </DarkFlex>
  )
}
